import { useInfiniteQuery } from "@tanstack/react-query";
import * as React from "react";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

import { boardQueryKeys } from "./boardQueryKeys";

/**
 * Every board the person can open in the current org: their own drafts, the
 * organization's shared boards and their own personal ones. `data` is one flat
 * `Board[]` across every page the board service returns: the hook requests
 * each next page as soon as the previous one arrives, with no "load more"
 * action.
 */
export function useBoards() {
  const { boardService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;

  const query = useInfiniteQuery({
    queryKey: boardQueryKeys.list(orgId ?? ""),
    enabled: orgId !== undefined,
    queryFn: async ({ pageParam, signal }) => {
      if (orgId === undefined) {
        throw new Error("CurrentOrganization is not set");
      }
      return boardService.getBoardsForOrg(orgId, {
        abortSignal: signal,
        pageToken: pageParam,
      });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_token,
    select: (data) => data.pages.flatMap((page) => page.items),
    // A board deleted in another tab or by another person must not stay in the
    // list. Writes in this tab invalidate the list; for other changes it is
    // refetched every minute while the tab is visible, and the 30-second
    // `staleTime` (the app default is 5 minutes) lets mounting the list and
    // returning to the tab refetch it too.
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } = query;

  // Stop on error: `hasNextPage` stays true after a failed page fetch, so
  // refiring would retry forever.
  React.useEffect(
    function loadAllPages() {
      if (hasNextPage && !isFetchingNextPage && !isError) {
        void fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, isError, fetchNextPage],
  );

  return query;
}
